/**
 * FHIR R4 Mapper Utilities
 * Converts existing Firestore data to FHIR R4 compliant JSON resources.
 * US Core profiles are used where applicable.
 *
 * LOINC codes used:
 *   BMI      : 39156-5
 *   Height   : 8302-2
 *   Weight   : 29463-7
 *   BP Panel : 55284-4
 *   BP Sys   : 8480-6
 *   BP Dia   : 8462-4
 *   Heart Rate: 8867-4
 */

const FHIR_VERSION = "4.0.1";
const US_CORE_BASE = "http://hl7.org/fhir/us/core/StructureDefinition";
const LOINC = "http://loinc.org";
const UCUM = "http://unitsofmeasure.org";
const SNOMED = "http://snomed.info/sct";
const HL7_CONDITION_CLINICAL = "http://terminology.hl7.org/CodeSystem/condition-clinical";
const HL7_COVERAGE_CLASS = "http://terminology.hl7.org/CodeSystem/coverage-class";
const HL7_ACT_CODE = "http://terminology.hl7.org/CodeSystem/v3-ActCode";
const HL7_ALLERGY_CLINICAL = "http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical";
const HL7_MED_STATEMENT_STATUS = "active";
const AIGI_SYSTEM = "https://aigicare.health";

// ─────────────────────────────────────────────
// PATIENT
// ─────────────────────────────────────────────

/**
 * Maps a users/ document to a FHIR R4 Patient resource.
 * @param {object} userData - Firestore users/ document fields
 * @param {string} userId - Firestore document ID
 * @returns {object} FHIR Patient resource
 */
export const mapUserToFHIRPatient = (userData, userId) => {
  const nameParts = (userData.fullName || "").trim().split(" ");
  const given = nameParts.slice(0, -1).length > 0 ? nameParts.slice(0, -1) : [nameParts[0] || ""];
  const family = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";

  const telecom = [];
  if (userData.email) {
    telecom.push({ system: "email", value: userData.email, use: "home" });
  }
  if (userData.phoneNumber) {
    telecom.push({ system: "phone", value: userData.phoneNumber, use: "mobile" });
  }

  const genderRaw = (userData.gender || "").toLowerCase();
  const gender =
    genderRaw === "male" ? "male" :
    genderRaw === "female" ? "female" :
    genderRaw === "other" ? "other" : "unknown";

  return {
    resourceType: "Patient",
    id: userId,
    meta: {
      profile: [`${US_CORE_BASE}/us-core-patient`],
      lastUpdated: new Date().toISOString(),
    },
    identifier: [
      {
        use: "official",
        system: `${AIGI_SYSTEM}/patient-id`,
        value: userId,
      },
    ],
    active: userData.status !== "inactive",
    name: [
      {
        use: "official",
        text: userData.fullName || "",
        family,
        given,
      },
    ],
    telecom,
    gender,
    birthDate: userData.dob || null,
  };
};

// ─────────────────────────────────────────────
// OBSERVATIONS — VITALS
// ─────────────────────────────────────────────

/**
 * Maps user vitals (height, weight, BMI) to FHIR Observation resources.
 * @param {object} vitalsData - { height, weight, bmi }  (height in cm, weight in lbs)
 * @param {string} userId
 * @returns {Array} Array of FHIR Observation resources
 */
export const mapVitalsToFHIRObservations = (vitalsData, userId) => {
  const ts = new Date().toISOString();
  const observations = [];

  const base = {
    resourceType: "Observation",
    meta: { profile: [`${US_CORE_BASE}/us-core-vital-signs`] },
    status: "final",
    category: [
      {
        coding: [
          {
            system: "http://terminology.hl7.org/CodeSystem/observation-category",
            code: "vital-signs",
            display: "Vital Signs",
          },
        ],
      },
    ],
    subject: { reference: `Patient/${userId}` },
    effectiveDateTime: ts,
  };

  if (vitalsData.bmi != null && vitalsData.bmi !== "") {
    observations.push({
      ...base,
      id: `bmi-${userId}`,
      code: {
        coding: [{ system: LOINC, code: "39156-5", display: "Body mass index (BMI) [Ratio]" }],
        text: "BMI",
      },
      valueQuantity: {
        value: parseFloat(vitalsData.bmi),
        unit: "kg/m2",
        system: UCUM,
        code: "kg/m2",
      },
    });
  }

  if (vitalsData.height != null && vitalsData.height !== "") {
    observations.push({
      ...base,
      id: `height-${userId}`,
      code: {
        coding: [{ system: LOINC, code: "8302-2", display: "Body height" }],
        text: "Height",
      },
      valueQuantity: {
        value: parseFloat(vitalsData.height),
        unit: "cm",
        system: UCUM,
        code: "cm",
      },
    });
  }

  if (vitalsData.weight != null && vitalsData.weight !== "") {
    observations.push({
      ...base,
      id: `weight-${userId}`,
      code: {
        coding: [{ system: LOINC, code: "29463-7", display: "Body weight" }],
        text: "Weight",
      },
      valueQuantity: {
        value: parseFloat(vitalsData.weight),
        unit: "[lb_av]",
        system: UCUM,
        code: "[lb_av]",
      },
    });
  }

  return observations;
};

/**
 * Maps blood pressure values to a FHIR Observation (BP Panel).
 * @param {string|number} bpMm - Systolic mmHg
 * @param {string|number} bpHg - Diastolic mmHg
 * @param {string} userId
 * @param {string} sessionId - chatbot session ID
 * @returns {object} FHIR Observation resource
 */
export const mapBPToFHIRObservation = (bpMm, bpHg, userId, sessionId) => ({
  resourceType: "Observation",
  id: `bp-${sessionId}`,
  meta: { profile: [`${US_CORE_BASE}/us-core-blood-pressure`] },
  status: "final",
  category: [
    {
      coding: [
        {
          system: "http://terminology.hl7.org/CodeSystem/observation-category",
          code: "vital-signs",
          display: "Vital Signs",
        },
      ],
    },
  ],
  code: {
    coding: [{ system: LOINC, code: "55284-4", display: "Blood pressure systolic and diastolic" }],
    text: "Blood Pressure",
  },
  subject: { reference: `Patient/${userId}` },
  encounter: { reference: `Encounter/encounter-${sessionId}` },
  effectiveDateTime: new Date().toISOString(),
  component: [
    {
      code: {
        coding: [{ system: LOINC, code: "8480-6", display: "Systolic blood pressure" }],
      },
      valueQuantity: {
        value: parseFloat(bpMm),
        unit: "mmHg",
        system: UCUM,
        code: "mm[Hg]",
      },
    },
    {
      code: {
        coding: [{ system: LOINC, code: "8462-4", display: "Diastolic blood pressure" }],
      },
      valueQuantity: {
        value: parseFloat(bpHg),
        unit: "mmHg",
        system: UCUM,
        code: "mm[Hg]",
      },
    },
  ],
});

/**
 * Maps heart rate to a FHIR Observation.
 * @param {string|number} heartrate
 * @param {string} userId
 * @param {string} sessionId
 * @returns {object} FHIR Observation resource
 */
export const mapHRToFHIRObservation = (heartrate, userId, sessionId) => ({
  resourceType: "Observation",
  id: `hr-${sessionId}`,
  meta: { profile: [`${US_CORE_BASE}/us-core-heart-rate`] },
  status: "final",
  category: [
    {
      coding: [
        {
          system: "http://terminology.hl7.org/CodeSystem/observation-category",
          code: "vital-signs",
          display: "Vital Signs",
        },
      ],
    },
  ],
  code: {
    coding: [{ system: LOINC, code: "8867-4", display: "Heart rate" }],
    text: "Heart Rate",
  },
  subject: { reference: `Patient/${userId}` },
  encounter: { reference: `Encounter/encounter-${sessionId}` },
  effectiveDateTime: new Date().toISOString(),
  valueQuantity: {
    value: parseFloat(heartrate),
    unit: "/min",
    system: UCUM,
    code: "/min",
  },
});

// ─────────────────────────────────────────────
// COVERAGE (INSURANCE)
// ─────────────────────────────────────────────

/**
 * Maps an insurance_data/ document to a FHIR R4 Coverage resource.
 * @param {object} insuranceData - Firestore insurance_data/ document fields
 * @param {string} userId
 * @returns {object} FHIR Coverage resource
 */
export const mapInsuranceToCoverage = (insuranceData, userId) => {
  const coverageClass = [];

  if (insuranceData.group_id || insuranceData.groupID) {
    coverageClass.push({
      type: {
        coding: [{ system: HL7_COVERAGE_CLASS, code: "group", display: "Group" }],
      },
      value: insuranceData.group_id || insuranceData.groupID || "",
      name: insuranceData.group_name || insuranceData.groupName || "",
    });
  }

  return {
    resourceType: "Coverage",
    id: `coverage-${userId}`,
    meta: {
      profile: [`${US_CORE_BASE}/us-core-coverage`],
      lastUpdated: new Date().toISOString(),
    },
    status: "active",
    beneficiary: { reference: `Patient/${userId}` },
    payor: [
      {
        display: insuranceData.insurance_carrier || insuranceData.insuranceCarrier || "Unknown",
      },
    ],
    identifier: [
      {
        system: `${AIGI_SYSTEM}/policy-id`,
        value: insuranceData.policy_id || insuranceData.policyID || "",
      },
    ],
    class: coverageClass,
    period: insuranceData.expiry_date
      ? { end: insuranceData.expiry_date }
      : undefined,
  };
};

// ─────────────────────────────────────────────
// ENCOUNTER (CHATBOT SESSION)
// ─────────────────────────────────────────────

/**
 * Maps a chatbot/ session document to a FHIR R4 Encounter resource.
 * @param {object} sessionData - Firestore chatbot/ document fields
 * @param {string} userId
 * @param {string} sessionId
 * @returns {object} FHIR Encounter resource
 */
export const mapChatSessionToEncounter = (sessionData, userId, sessionId) => {
  const statusMap = {
    completed: "finished",
    active: "in-progress",
    abandoned: "cancelled",
  };
  const fhirStatus = statusMap[sessionData.status] || "in-progress";

  const encounter = {
    resourceType: "Encounter",
    id: `encounter-${sessionId}`,
    meta: {
      profile: [`${US_CORE_BASE}/us-core-encounter`],
      lastUpdated: new Date().toISOString(),
    },
    status: fhirStatus,
    class: {
      system: HL7_ACT_CODE,
      code: "AMB",
      display: "ambulatory",
    },
    type: [
      {
        coding: [{ system: SNOMED, code: "11429006", display: "Consultation" }],
        text: "Consultation",
      },
    ],
    subject: { reference: `Patient/${userId}` },
    period: {
      start:
        sessionData.created_at instanceof Object && sessionData.created_at.toDate
          ? sessionData.created_at.toDate().toISOString()
          : sessionData.created_at || new Date().toISOString(),
    },
  };

  if (sessionData.status === "completed" && sessionData.updated_at) {
    encounter.period.end =
      sessionData.updated_at instanceof Object && sessionData.updated_at.toDate
        ? sessionData.updated_at.toDate().toISOString()
        : sessionData.updated_at;
  }

  if (sessionData.illness) {
    encounter.reasonCode = [{ text: sessionData.illness }];
  }

  return encounter;
};

// ─────────────────────────────────────────────
// CONDITION (DIAGNOSIS)
// ─────────────────────────────────────────────

/**
 * Maps a diagnosis string (conditional_diagnosis) to a FHIR R4 Condition resource.
 * @param {string} diagnosis - Full AI-generated diagnosis text (stored in note)
 * @param {string} userId
 * @param {string} sessionId
 * @param {string|null} illness - Short illness name used for code.text (e.g. "Diarrhea")
 * @returns {object} FHIR Condition resource
 */
export const mapDiagnosisToCondition = (diagnosis, userId, sessionId, illness = null) => ({
  resourceType: "Condition",
  id: `condition-${sessionId}`,
  meta: {
    profile: [`${US_CORE_BASE}/us-core-condition-problems-health-concerns`],
    lastUpdated: new Date().toISOString(),
  },
  clinicalStatus: {
    coding: [{ system: HL7_CONDITION_CLINICAL, code: "active", display: "Active" }],
  },
  verificationStatus: {
    coding: [
      {
        system: "http://terminology.hl7.org/CodeSystem/condition-ver-status",
        code: "provisional",
        display: "Provisional",
      },
    ],
  },
  category: [
    {
      coding: [
        {
          system: "http://terminology.hl7.org/CodeSystem/condition-category",
          code: "encounter-diagnosis",
          display: "Encounter Diagnosis",
        },
      ],
    },
  ],
  // Use short illness name for code.text; full AI diagnosis goes in note
  code: { text: illness || diagnosis },
  subject: { reference: `Patient/${userId}` },
  encounter: { reference: `Encounter/encounter-${sessionId}` },
  recordedDate: new Date().toISOString(),
  // Store full AI diagnosis text in note when illness name is separately provided
  ...(illness ? { note: [{ text: diagnosis }] } : {}),
});

// ─────────────────────────────────────────────
// CARE PLAN
// ─────────────────────────────────────────────

/**
 * Maps plan_of_care string to a FHIR R4 CarePlan resource.
 * @param {string} planOfCare
 * @param {string} userId
 * @param {string} sessionId
 * @returns {object} FHIR CarePlan resource
 */
export const mapPlanOfCareToCarePlan = (planOfCare, userId, sessionId) => ({
  resourceType: "CarePlan",
  id: `careplan-${sessionId}`,
  meta: {
    profile: [`${US_CORE_BASE}/us-core-careplan`],
    lastUpdated: new Date().toISOString(),
  },
  status: "active",
  intent: "plan",
  title: "Plan of Care",
  description: planOfCare,
  subject: { reference: `Patient/${userId}` },
  encounter: { reference: `Encounter/encounter-${sessionId}` },
  created: new Date().toISOString(),
});

// ─────────────────────────────────────────────
// MEDICAL REPORTS → FHIR (doc_type based routing)
// ─────────────────────────────────────────────

/**
 * Maps a medical_reports/ document to the correct FHIR resource based on doc_type.
 * @param {object} reportData - Firestore medical_reports/ document fields
 * @param {string} userId
 * @returns {object|null} FHIR resource or null if doc_type not mapped
 */
export const mapMedicalReportToFHIR = (reportData, userId) => {
  const docType = reportData.doc_type || "";
  const text = reportData.doc_summary || reportData.doc_text || reportData.file_name || "";
  // Use reports_id + docType for unique stable ID when Firestore doc_id not yet available
  const reportId = reportData.doc_id || reportData.id
    || (reportData.reports_id ? `${reportData.reports_id}-${docType}` : `${userId}-${docType}-${Date.now()}`);

  switch (docType) {
    case "allergy":
      return {
        resourceType: "AllergyIntolerance",
        id: `allergy-${reportId}`,
        meta: {
          profile: [`${US_CORE_BASE}/us-core-allergyintolerance`],
          lastUpdated: new Date().toISOString(),
        },
        clinicalStatus: {
          coding: [{ system: HL7_ALLERGY_CLINICAL, code: "active", display: "Active" }],
        },
        patient: { reference: `Patient/${userId}` },
        code: { text },
        recordedDate: new Date().toISOString(),
      };

    case "medications":
      return {
        resourceType: "MedicationStatement",
        id: `med-${reportId}`,
        meta: {
          lastUpdated: new Date().toISOString(),
        },
        status: HL7_MED_STATEMENT_STATUS,
        subject: { reference: `Patient/${userId}` },
        medicationCodeableConcept: { text },
        dateAsserted: new Date().toISOString(),
      };

    case "medical_condition":
    case "medical_conditions":
      return {
        resourceType: "Condition",
        id: `condition-report-${reportId}`,
        meta: {
          profile: [`${US_CORE_BASE}/us-core-condition-problems-health-concerns`],
          lastUpdated: new Date().toISOString(),
        },
        clinicalStatus: {
          coding: [{ system: HL7_CONDITION_CLINICAL, code: "active", display: "Active" }],
        },
        category: [
          {
            coding: [
              {
                system: "http://terminology.hl7.org/CodeSystem/condition-category",
                code: "problem-list-item",
                display: "Problem List Item",
              },
            ],
          },
        ],
        code: { text },
        subject: { reference: `Patient/${userId}` },
        recordedDate: new Date().toISOString(),
      };

    case "past_surgical_history": {
      // Skip if patient answered "no", "none", "n/a" or similar
      const normalizedText = text.trim().toLowerCase();
      if (!normalizedText || ["no", "none", "n/a", "na", "nil", "nothing", "no history"].includes(normalizedText)) {
        return null;
      }
      return {
        resourceType: "Procedure",
        id: `procedure-${reportId}`,
        meta: {
          profile: [`${US_CORE_BASE}/us-core-procedure`],
          lastUpdated: new Date().toISOString(),
        },
        status: "completed",
        code: { text },
        subject: { reference: `Patient/${userId}` },
        note: [{ text }],
      };
    }

    case "social_history":
      return {
        resourceType: "Observation",
        id: `social-${reportId}`,
        meta: {
          lastUpdated: new Date().toISOString(),
        },
        status: "final",
        category: [
          {
            coding: [
              {
                system: "http://terminology.hl7.org/CodeSystem/observation-category",
                code: "social-history",
                display: "Social History",
              },
            ],
          },
        ],
        code: {
          coding: [{ system: LOINC, code: "29762-2", display: "Social history Narrative" }],
          text: "Social History",
        },
        subject: { reference: `Patient/${userId}` },
        valueString: text,
        effectiveDateTime: new Date().toISOString(),
      };

    case "other_report":
    case "upload_report":
    default:
      return {
        resourceType: "DocumentReference",
        id: `docref-${reportId}`,
        meta: {
          lastUpdated: new Date().toISOString(),
        },
        status: "current",
        type: { text: docType || "Medical Document" },
        subject: { reference: `Patient/${userId}` },
        date: new Date().toISOString(),
        content: [
          {
            attachment: {
              url: reportData.doc_url || reportData.file_url || "",
              title: reportData.file_name || docType || "Medical Report",
            },
          },
        ],
      };
  }
};

// ─────────────────────────────────────────────
// PRACTITIONER (PCP)
// ─────────────────────────────────────────────

/**
 * Maps PCP details to a FHIR R4 Practitioner resource.
 * @param {object} pcpData - { pcp_name, pcp_phone, pcp_fax, pcp_address }
 * @param {string} userId - used to scope the resource ID to the patient
 * @returns {object} FHIR Practitioner resource
 */
export const mapPCPToPractitioner = (pcpData, userId) => {
  const telecom = [];
  if (pcpData.pcp_phone) telecom.push({ system: "phone", value: pcpData.pcp_phone, use: "work" });
  if (pcpData.pcp_fax) telecom.push({ system: "fax", value: pcpData.pcp_fax, use: "work" });
  if (pcpData.pcp_email) telecom.push({ system: "email", value: pcpData.pcp_email, use: "work" });

  const address = pcpData.pcp_address
    ? [{ use: "work", text: pcpData.pcp_address }]
    : [];

  return {
    resourceType: "Practitioner",
    id: `practitioner-${userId}`,
    meta: {
      profile: [`${US_CORE_BASE}/us-core-practitioner`],
      lastUpdated: new Date().toISOString(),
    },
    name: pcpData.pcp_name
      ? [{ use: "official", text: pcpData.pcp_name }]
      : [],
    telecom,
    address,
  };
};
